import React, { useCallback, useEffect, useState } from "react";
import { Button, Modal, ModalBody, ModalFooter, ModalHeader } from "reactstrap";
import { useIntl } from "react-intl";
import { useMeCore } from "../../../../cores/me";
import { useBoardCore } from "../../../../cores/boards";
import { BoardType } from "../../../../cores/boards/constants/board-type.constant";
import { InputWembii } from "../../InputWembii";

interface Props {
  isOpen: boolean;
  leadCount: number;
  isSubmitting?: boolean;
  onClose: () => void;
  onConfirm: (boardId: string, listId: string) => void;
}

export default function SendLeadsToCrmModal({
  isOpen,
  leadCount,
  isSubmitting = false,
  onClose,
  onConfirm,
}: Props) {
  const intl = useIntl();
  const { company } = useMeCore();
  const { boards, listNames, getBusinessBoards, getLists } = useBoardCore();
  const [boardId, setBoardId] = useState("");
  const [listId, setListId] = useState("");

  useEffect(() => {
    if (isOpen && company?.id) {
      getBusinessBoards(company.id);
    }
  }, [company?.id, getBusinessBoards, isOpen]);

  useEffect(() => {
    if (!isOpen) {
      setBoardId("");
      setListId("");
      return;
    }

    const businessBoards = boards.filter((board) => board.type === BoardType.BUSINESS);
    if (businessBoards.length && !boardId) {
      setBoardId(businessBoards[0].id);
    }
  }, [boardId, boards, isOpen]);

  useEffect(() => {
    if (isOpen && company?.id && boardId) {
      getLists(company.id, boardId);
    }
  }, [boardId, company?.id, getLists, isOpen]);

  useEffect(() => {
    if (listNames.length) {
      setListId((current) => current || listNames[0].id);
    } else {
      setListId("");
    }
  }, [listNames]);

  const businessBoards = boards.filter((board) => board.type === BoardType.BUSINESS);
  const canSubmit = !!boardId && !!listId && !isSubmitting;

  const handleBoardChange = useCallback((nextBoardId: string) => {
    setBoardId(nextBoardId);
    setListId("");
  }, []);

  const handleConfirm = useCallback(() => {
    if (!canSubmit) return;
    onConfirm(boardId, listId);
  }, [boardId, canSubmit, listId, onConfirm]);

  return (
    <Modal isOpen={isOpen} toggle={onClose} centered>
      <ModalHeader toggle={onClose}>
        {intl.formatMessage({ id: "app.facebookForm.sendToCrm.title" })}
      </ModalHeader>
      <ModalBody>
        <p className="text-muted mb-3">
          {intl.formatMessage(
            { id: "app.facebookForm.sendToCrm.description" },
            { count: leadCount }
          )}
        </p>
        {businessBoards.length === 0 ? (
          <p className="mb-0 text-warning">
            {intl.formatMessage({ id: "app.facebookForm.sendToCrm.noBoards" })}
          </p>
        ) : (
          <>
            <InputWembii
              type="select"
              label={intl.formatMessage({ id: "app.sidebar.crm.business" })}
              value={boardId}
              onChange={handleBoardChange}
            >
              {businessBoards.map((board) => (
                <option key={board.id} value={board.id}>
                  {board.name}
                </option>
              ))}
            </InputWembii>
            <InputWembii
              type="select"
              label={intl.formatMessage({ id: "app.crmBusiness.list" })}
              value={listId}
              onChange={setListId}
            >
              {listNames.map((list) => (
                <option key={list.id} value={list.id}>
                  {list.name}
                </option>
              ))}
            </InputWembii>
          </>
        )}
      </ModalBody>
      <ModalFooter>
        <Button color="secondary" outline onClick={onClose} disabled={isSubmitting}>
          {intl.formatMessage({ id: "app.socialMediaPost.cancel" })}
        </Button>
        <Button color="primary" onClick={handleConfirm} disabled={!canSubmit}>
          {intl.formatMessage({ id: "app.facebookForm.sendToCrm.confirm" })}
        </Button>
      </ModalFooter>
    </Modal>
  );
}
